/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * Lab compare on the category page (ticket "Lab Charts Off the Card", 2026-09-08): the
 * page-level state behind the two surfaces that reuse the product page's chart panel —
 *
 *  - the **dialog** a card's versus-yours sparkline opens (this card versus the declared
 *    pair, on the tapped measure);
 *  - the **pinned drawer**: up to `MAX_PINS` cards overlaid on one chart with the declared
 *    pair, driven from "Pin to compare" on the cards.
 *
 * Page state only — nothing persists across visits; the declaration does (`declared.ts`).
 *
 * Drawer states (ticket "compare drawer: open, reduce, close", 2026-09-09): while pins exist
 * the drawer is either OPEN (the panel) or COLLAPSED (a docked pill) — never invisible, so the
 * comparison the reader built is always one click away. No pins = nothing rendered. Pinning
 * opens on desktop and only docks the bar on a phone (`pin(target, open)`); `closeDrawer`
 * collapses; `clearPins` empties the set.
 *
 * The focused pick (2026-10-18, one desktop panel): on a guide at `lg` and up the action row's
 * Measurements and Owners open the same side panel the pins live in, on that pick — the
 * panel's "This pick" and "Owners" segments, next to "Compare". Before this the grid card
 * opened a centred modal and the in-depth card expanded inline: three surfaces for one row.
 * A focus with no pins is a reason to render the panel; the pill (collapsed) still needs pins.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { LAB_PEER_COLORS, type LabTabKey } from 'lib/dealhunt/audioChart'
import { productSlugFromHref } from 'lib/dealhunt/url'
import { THIS_COLOR } from './curves'
import type { DeclaredProduct } from './declared'
import type { DSGuidePickRef } from 'types.sg.ds'

/** A product the lab surfaces draw: identity, its lab id, and where its page is. */
export interface LabTarget {
  nodeId: string
  slug: string | null
  name: string
  pdpHref: string | null
  clearbuyProductId: number | null
}

export interface LabDialogRequest {
  target: LabTarget
  tab: LabTabKey
}

/** Products in one comparison, `/compare`'s columns: where the drawer draws the declared pair it
 *  takes one of them, so the pins get the rest (`maxPins`, 2026-10-30). */
export const MAX_PINS = 3

/** Content width from which the wide chart frame (600 × 250) reads better than the tall one —
 *  one rule for every host of `LabCompareBody` (the drawer, the phone pick sheet). */
export const WIDE_FRAME_PX = 520

/** The chart frame for a host whose content is `contentW` px wide: the host's own width, never
 *  the breakpoint, and derived — never observed (Directive 427141fd). */
export function chartFrameFor(contentW: number): 'compact' | 'wide' {
  return contentW >= WIDE_FRAME_PX ? 'wide' : 'compact'
}

/** Where a guide pick's row sits — the `location` its events report, and the panel's pin. */
export type PickPlacement = 'roster' | 'section' | 'grid'

/** The panel's segments: the focused pick alone, its owners, or the pinned comparison. */
export type PanelSegment = 'measure' | 'owners' | 'compare'

/** `/compare`'s tokens for the declared pair, then these targets (the page keeps three): each
 *  one's slug from its product page href, else its node id, which `/compare` redirects. */
export function compareTokensFor(
  declared: Pick<DeclaredProduct, 'nodeId' | 'href'> | null,
  targets: readonly Pick<LabTarget, 'nodeId' | 'pdpHref'>[]
): string[] {
  const tokens = [
    declared?.nodeId ? productSlugFromHref(declared.href) ?? declared.nodeId : null,
    ...targets.map(t => productSlugFromHref(t.pdpHref) ?? t.nodeId),
  ]
  return Array.from(new Set(tokens.filter((t): t is string => Boolean(t))))
}

/** The pick the desktop panel is on, and which of its segments shows. */
export interface PickFocus {
  pick: DSGuidePickRef
  placement: PickPlacement
  segment: PanelSegment
}

/** Product colours in order, on every surface that compares products (pins, review versus panel,
 *  compare page columns and curves): this-card cyan, then the lab's yellow and blue. Red is
 *  skipped (yours), and no winner mark may use any of these (winners are amber). */
export const PIN_COLORS = [THIS_COLOR, LAB_PEER_COLORS[0], LAB_PEER_COLORS[2]]

interface LabCompareState {
  dialog: LabDialogRequest | null
  openDialog: (target: LabTarget, tab: LabTabKey) => void
  closeDialog: () => void
  pinned: LabTarget[]
  /** How many cards can be pinned: `MAX_PINS` less the declared pair where the host draws it, so
   *  the drawer's comparison is always one `/compare` can open whole. */
  maxPins: number
  /** Pin, or refuse when full — returns whether it was pinned. `open` (the default) also opens
   *  the drawer; false leaves it as it was, so a phone keeps the list under the thumb and the
   *  collapsed bar says what was pinned (2026-10-17). A refusal always opens it: the reader
   *  sees why. */
  pin: (target: LabTarget, open?: boolean) => boolean
  unpin: (nodeId: string) => void
  /** Empty the set — the drawer goes away (no pins, nothing to show). With `next`, the set
   *  becomes those (up to `maxPins`) and the panel opens on them. */
  clearPins: (next?: readonly LabTarget[]) => void
  isPinned: (nodeId: string) => boolean
  /** True = the panel; false with pins = the collapsed pill. */
  drawerOpen: boolean
  openDrawer: () => void
  /** Collapse to the pill — the pins stay. With no pins there is no pill: the focus goes too. */
  closeDrawer: () => void
  /** The pick the panel is on (desktop), or null: the panel is the comparison alone. */
  focus: PickFocus | null
  /** Open the panel on this pick's measurements or owners (the row's two disclosure slots). */
  focusPick: (pick: DSGuidePickRef, placement: PickPlacement, segment: 'measure' | 'owners') => void
  /** Switch the focused panel's segment; no focus, no-op. */
  setSegment: (segment: PanelSegment) => void
  clearFocus: () => void
}

const LabCompareContext = createContext<LabCompareState | null>(null)

export const LabCompareProvider: React.FC<{
  /** Comparison slots the host fills itself: 1 where the drawer draws the declared pair. */
  reserved?: number
  children?: React.ReactNode
}> = ({ reserved = 0, children }) => {
  const maxPins = MAX_PINS - reserved
  const [dialog, setDialog] = useState<LabDialogRequest | null>(null)
  const [kept, setPinned] = useState<LabTarget[]>([])
  // Declaring a pair with every slot pinned: the last pin gives up its slot to yours, and gets it
  // back if the pair goes — hidden, never dropped.
  const pinned = kept.length > maxPins ? kept.slice(0, maxPins) : kept
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [focus, setFocus] = useState<PickFocus | null>(null)
  // The latest focus, for callbacks that must not re-create on every focus change (`pin` is
  // what every card's button holds).
  const focusRef = useRef<PickFocus | null>(null)
  focusRef.current = focus

  // Pinning while the panel is on a pick shows the comparison the pin just joined; with no
  // focus the panel IS the comparison.
  const showCompare = (): void => {
    if (focusRef.current) {
      setFocus(f => (f && f.segment !== 'compare' ? { ...f, segment: 'compare' } : f))
    }
  }

  const openDialog = useCallback((target: LabTarget, tab: LabTabKey) => {
    setDialog({ target, tab })
  }, [])
  const closeDialog = useCallback(() => setDialog(null), [])
  const pin = useCallback(
    (target: LabTarget, open = true): boolean => {
      if (pinned.some(p => p.nodeId === target.nodeId)) {
        setDrawerOpen(true)
        showCompare()
        return true
      }
      if (pinned.length >= maxPins) {
        setDrawerOpen(true)
        showCompare()
        return false
      }
      // Two pins in one handler (the review's "Compare these scores with" pins the reviewed
      // product and the rival) share this render's `pinned`: dedupe and cap on the latest set.
      setPinned(prev => {
        const full = prev.length >= maxPins || prev.some(p => p.nodeId === target.nodeId)
        return full ? prev : [...prev, target]
      })
      if (open) {
        setDrawerOpen(true)
        showCompare()
      }
      return true
    },
    [pinned, maxPins]
  )
  const unpin = useCallback(
    (nodeId: string) => {
      // The last pin out of a focused panel's comparison stays on Compare (2026-10-19): it has
      // its empty state now, with the pick's pin tile right below.
      setPinned(kept.filter(p => p.nodeId !== nodeId))
    },
    [kept]
  )
  const clearPins = useCallback(
    (next: readonly LabTarget[] = []) => {
      setPinned(next.slice(0, maxPins))
      if (next.length) {
        setDrawerOpen(true)
        showCompare()
        return
      }
      // A focused panel stays where it is (Compare shows its empty state); with no focus there
      // is nothing left to show.
      if (!focusRef.current) {
        setDrawerOpen(false)
      }
    },
    [maxPins]
  )
  const isPinned = useCallback((nodeId: string) => pinned.some(p => p.nodeId === nodeId), [pinned])
  const openDrawer = useCallback(() => setDrawerOpen(true), [])
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false)
    if (pinned.length === 0) {
      setFocus(null)
    }
  }, [pinned.length])
  const focusPick = useCallback(
    (pick: DSGuidePickRef, placement: PickPlacement, segment: 'measure' | 'owners') => {
      setFocus({ pick, placement, segment })
      setDrawerOpen(true)
    },
    []
  )
  const setSegment = useCallback((segment: PanelSegment) => {
    setFocus(f => (f ? { ...f, segment } : f))
  }, [])
  const clearFocus = useCallback(() => setFocus(null), [])

  const value = useMemo<LabCompareState>(
    () => ({
      dialog,
      openDialog,
      closeDialog,
      pinned,
      maxPins,
      pin,
      unpin,
      clearPins,
      isPinned,
      drawerOpen,
      openDrawer,
      closeDrawer,
      focus,
      focusPick,
      setSegment,
      clearFocus,
    }),
    [
      dialog,
      openDialog,
      closeDialog,
      pinned,
      maxPins,
      pin,
      unpin,
      clearPins,
      isPinned,
      drawerOpen,
      openDrawer,
      closeDrawer,
      focus,
      focusPick,
      setSegment,
      clearFocus,
    ]
  )
  return <LabCompareContext.Provider value={value}>{children}</LabCompareContext.Provider>
}

/** The page's lab-compare state, or null outside a provider (search results, PDP rails). */
export function useLabCompare(): LabCompareState | null {
  return useContext(LabCompareContext)
}
